import { defaultSortComparator } from '@components/DataGrid/types';
import { createSelector } from '@reduxjs/toolkit';
import { ParsedDate, parseIsoDate } from '../../types';
import { Port } from '../Lookups/types';
import { Location } from '../Location/types';

export interface Vessel {
	id: string;
	name: string;
}

export interface FetchVesselRequest {
	startsWith?: string;
}

export function sortVesselResponse(vessels: Vessel[]) {
	return vessels.sort((a, b) => {
		return defaultSortComparator(a.name, b.name);
	});
}

export interface VoyageRaw {
	id: number;
	transType: string;
	sts: string;
	trmId?: string;
	trmName?: string;
	voyageIn?: string;
	fromPortId?: string;
	fromPortname?: string;
	cntrMoves?: number;
	voyageOut?: string;
	toPortId?: string;
	toPortname?: string;
	etaDate?: string;
	inEtdDate?: string;
	lastFreeDate?: string;
	outEtdDate?: string;
	cutoffDate?: string;
	frstRcvDate?: string;
}

export type Voyage = Omit<
	VoyageRaw,
	| 'etaDate'
	| 'inEtdDate'
	| 'lastFreeDate'
	| 'outEtdDate'
	| 'cutoffDate'
	| 'frstRcvDate'
	| 'fromPortId'
	| 'fromPortname'
	| 'toPortId'
	| 'toPortname'
	| 'trmId'
	| 'trmName'
> & {
	id: number;
	voyageIn?: string;
	voyageOut?: string;
	cutoffParsedDate?: ParsedDate;
	lastFreeParsedDate?: ParsedDate;
	outEtdParseDate?: ParsedDate;
	firstReceivedDate?: ParsedDate;
	inEtdParsedDate?: ParsedDate;
	etaParsedDate?: ParsedDate;
	transType?: string;
	sts?: string;

	terminal: Location | null;
	fromPort: Port | null;
	toPort: Port | null;
};

export function transformVoyageResponse({
	id,
	etaDate,
	inEtdDate,
	lastFreeDate,
	outEtdDate,
	cutoffDate,
	frstRcvDate,
	trmId,
	trmName,
	fromPortId,
	fromPortname,
	toPortId,
	toPortname,
	...voyage
}: VoyageRaw) {
	return {
		id: id,
		firstReceivedDate: parseIsoDate(frstRcvDate),
		cutoffParsedDate: parseIsoDate(cutoffDate),
		lastFreeParsedDate: parseIsoDate(lastFreeDate),
		outEtdParseDate: parseIsoDate(outEtdDate),
		inEtdParsedDate: parseIsoDate(inEtdDate),
		etaParsedDate: parseIsoDate(etaDate),
		terminal: trmId ? { id: trmId, name: trmName } : null,
		fromPort: fromPortId ? { id: fromPortId, name: fromPortname } : null,
		toPort: toPortId ? { id: toPortId, name: toPortname } : null,
		...voyage
	} as Voyage;
}

export interface FetchVoyagesRequest {
	vesselId: string;
	catTransType?: string;
}

export interface FetchVoyageRequest {
	vesselId: string;
	voyageId: string;
}

export type VoyageWithVesselInfo = Voyage & {
	vesselId: string;
	isExport: boolean;
};

/** RTK Selector for enriching voyages with vesselId and isExport */
export const selectVoyagesWithVesselInfo = createSelector(
	(voyages: Voyage[] | null | undefined) => voyages,
	(_: Voyage[] | null | undefined, vesselId: string) => vesselId,
	(_: Voyage[] | null | undefined, _vesselId: string, isExport: boolean) => isExport,
	(voyages, vesselId, isExport): VoyageWithVesselInfo[] => {
		if (!voyages) return [];
		return (
			voyages
				.map((v) => ({ ...v, vesselId, isExport }))
				/** The API already returns voyages in name order, but a newly added one is pushed onto the
				 * end of the cached list (see updateVoyageCache) — re-sorting keeps it in its place until
				 * the next refetch. */
				.sort((a, b) =>
					defaultSortComparator(isExport ? (a.voyageOut ?? '') : (a.voyageIn ?? ''), isExport ? (b.voyageOut ?? '') : (b.voyageIn ?? ''))
				)
		);
	}
);

/** RTK Selector for filtering the voyages by the search text input */
export const selectFilteredVoyages = createSelector(
	(voyages: Voyage[] | null | undefined) => voyages,
	(_: Voyage[] | null | undefined, searchInput: string) => searchInput,
	(voyages, searchInput) => {
		if (!voyages) return [];

		if (!searchInput) return voyages;

		return voyages.filter(
			(v) =>
				v.id.toString().includes(searchInput.toLowerCase()) ||
				v.voyageIn?.toLowerCase().includes(searchInput.toLowerCase()) ||
				v.voyageOut?.toLowerCase().includes(searchInput.toLowerCase())
		);
	}
);
